import {
	keepPreviousData,
	useInfiniteQuery,
	useMutation,
} from "@tanstack/react-query"
import {
	ErrorResponse,
	InfinitivePage,
	SuccessCursorResponse,
	useHttpClient,
} from "api/api"
import { PatientUtilization } from "interface/billing"
import { SortValues } from "interface/sort"
import { useParams } from "react-router"

const queryKeys = {
	document: "document",
	utilization: "utilization",
}

export const usePatientDocument = (patientId: string) => {
	const { httpClient } = useHttpClient()

	return useMutation<
		{ blob: Blob; filename: string; download?: boolean },
		ErrorResponse,
		string
	>({
		mutationKey: [queryKeys.document],
		mutationFn: async (documentType: string) => {
			const response = await httpClient.get<Blob>(
				`/api/v1/billing/patients/${patientId}/exports/${documentType}`,
				{ responseType: "blob", validateStatus: () => true },
			)

			const contentType = response.headers["content-type"]

			if (contentType?.includes("application/json")) {
				const text = await response.data.text() // Read blob as text
				const json = JSON.parse(text)
				throw new Error(json.message || "Download failed")
			}

			const contentDisposition = response.headers["content-disposition"]
			let filename = `${patientId}-${documentType}`

			if (contentDisposition) {
				const match = contentDisposition.match(
					/filename[^;=\n]*=["']?([^"'\n]*)["']?/,
				)
				if (match?.[1]) {
					filename = match[1]
				}
			}

			return { blob: response.data, filename }
		},
	})
}

export const useMedicalNecessityFormDocument = (enrollmentId: string) => {
	const { httpClient } = useHttpClient()

	return useMutation<
		{ blob: Blob; filename: string; download?: boolean },
		ErrorResponse
	>({
		mutationKey: [queryKeys.document],
		mutationFn: async () => {
			const response = await httpClient.get<Blob>(
				`/api/v1/billing/enrollments/${enrollmentId}`,
				{ responseType: "blob", validateStatus: () => true },
			)

			const contentType = response.headers["content-type"]

			if (contentType?.includes("application/json")) {
				const text = await response.data.text() // Read blob as text
				const json = JSON.parse(text)
				throw new Error(json.message || "Download failed")
			}

			const contentDisposition = response.headers["content-disposition"]
			let filename = `${enrollmentId}-medical-necessity`

			if (contentDisposition) {
				const match = contentDisposition.match(
					/filename[^;=\n]*=["']?([^"'\n]*)["']?/,
				)
				if (match?.[1]) {
					filename = match[1]
				}
			}

			return { blob: response.data, filename }
		},
	})
}

export const usePatientsUtilization = ({
	month,
	sort,
	query,
}: {
	month?: string
	sort: SortValues<PatientUtilization>
	query?: string
}) => {
	const { httpClient } = useHttpClient()
	const { scopeId } = useParams()
	const params = {
		limit: 20,
		month,
		sort,
		query: query?.trim() || undefined,
		scopeId,
	}

	return useInfiniteQuery<
		InfinitivePage<PatientUtilization>,
		Error,
		{ pages: PatientUtilization[] }
	>({
		queryKey: [queryKeys.utilization, scopeId, params],
		queryFn: async ({ pageParam, signal }) => {
			const {
				data: { data },
			} = await httpClient.get<SuccessCursorResponse<PatientUtilization>>(
				`/api/v1/billing/${scopeId}/patient-utilization`,
				{
					signal,
					params: {
						...params,
						cursor: pageParam,
					},
				},
			)

			return {
				data: data.items,
				nextCursor: data.nextCursor,
			}
		},
		initialPageParam: undefined,
		placeholderData: keepPreviousData,
		select: ({ pages }) => {
			return {
				pages: pages.flatMap(({ data }) => data),
			}
		},
		getNextPageParam: (lastPage) => lastPage.nextCursor,
	})
}
